import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIstDayMon } from '../features/payments/paymentDates';
import type { HelpSection } from './content/types';
import styles from './Help.module.css';

export interface ZoomTarget {
  src: string;
  alt: string;
}

interface Props {
  section: HelpSection;
  /** Heading level for the section title: 2 on the Help page, 3 in the side sheet. */
  level?: 2 | 3;
  /** In-app link clicked (`/help/x`, `/payments`). */
  onNavigate: (to: string) => void;
  onZoom: (target: ZoomTarget) => void;
  /** Extra controls beside the title (Copy link). */
  actions?: ReactNode;
  id?: string;
}

/** One help section: title, "Updated <date>", the sanitised body and its screenshots. */
export function HelpArticle({ section, level = 2, onNavigate, onZoom, actions, id }: Props) {
  const { t } = useTranslation();
  const Heading = level === 2 ? 'h2' : 'h3';
  const onClick = (e: MouseEvent<HTMLElement>) => {
    const el = e.target as HTMLElement;
    const zoom = el.closest<HTMLElement>('[data-help-zoom]');
    if (zoom) {
      const img = zoom.querySelector('img');
      if (img) onZoom({ src: img.currentSrc || img.src, alt: img.alt });
      return;
    }
    const link = el.closest<HTMLAnchorElement>('a[data-internal]');
    if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      onNavigate(link.getAttribute('href') ?? '/help');
    }
  };
  return (
    <article id={id} tabIndex={-1} className={styles.article} aria-labelledby={`${id ?? section.id}-title`}>
      <div className={styles.articleHead}>
        <Heading id={`${id ?? section.id}-title`} className={level === 2 ? styles.articleTitle : styles.sheetTitle}>
          {section.title}
        </Heading>
        {actions}
      </div>
      <p className={styles.updated}>{t('help.updated', { date: formatIstDayMon(`${section.updated}T12:00:00+05:30`) })}</p>
      <div className={styles.prose} onClick={onClick} dangerouslySetInnerHTML={{ __html: section.html }} />
      {section.figures.length > 0 && (
        <div className={styles.figures}>
          {section.figures.map((f) =>
            f.src ? (
              <figure key={f.file} className={styles.figure}>
                <button
                  type="button"
                  className="help-zoom"
                  onClick={() => onZoom({ src: f.src as string, alt: f.caption })}
                  aria-label={t('help.zoom.open', { caption: f.caption })}
                >
                  <img src={f.src} alt={f.caption} loading="lazy" decoding="async" width={f.size?.[0]} height={f.size?.[1]} />
                </button>
                <figcaption>{f.caption}</figcaption>
              </figure>
            ) : null,
          )}
        </div>
      )}
    </article>
  );
}
